import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { Info, PenIcon, Trash2Icon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { HCPCloudTokenForList } from '@/lib/queries/terraformBackend';

const columnHelper = createColumnHelper<HCPCloudTokenForList>();

export const getColumns = (
  deleteToken: (id: string) => Promise<void>,
): ColumnDef<HCPCloudTokenForList>[] =>
  [
    columnHelper.accessor('name', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.hcpCloud.list.table"
          type="sort-only"
        />
      ),
      size: 15,
    }),
    columnHelper.accessor('workspace', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.hcpCloud.list.table"
          type="sort-only"
        />
      ),
      size: 15,
    }),
    columnHelper.accessor('url', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.hcpCloud.list.table"
          type="sort-only"
        />
      ),
      size: 20,
    }),
    columnHelper.accessor('status', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.terraformBackend.hcpCloud.list.table"
          optionsTranslationPath="integrations.terraformBackend.hcpCloud.list.table.status.options"
          type="select"
        />
      ),
      cell: ({ getValue, row: { original } }) => {
        const status = getValue();

        switch (status) {
          case 'active':
            return (
              <FormattedMessage id="integrations.terraformBackend.hcpCloud.list.table.status.options.active" />
            );
          case 'inactive':
            return (
              <FormattedMessage id="integrations.terraformBackend.hcpCloud.list.table.status.options.inactive" />
            );
          case 'error':
            return (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p>
                      🔴{' '}
                      <Link
                        to="/integrations/terraform-backend/hcp-cloud/$tokenId"
                        params={{ tokenId: original.id }}
                        className="underline hover:text-blue-500"
                      >
                        Update your HCP Cloud token
                      </Link>
                      <Info className="inline h-3 w-3 text-gray-500 ml-1" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[400px]">
                    <FormattedMessage id="integrations.terraformBackend.hcpCloud.list.table.status.error.tooltip" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
        }
      },
      size: 20,
    }),
    columnHelper.accessor('id', {
      header: '',
      cell: ({ getValue }) => (
        <Link
          to="/integrations/terraform-backend/hcp-cloud/$tokenId"
          params={{ tokenId: getValue() }}
        >
          <Button variant="link" className="text-xs">
            <FormattedMessage id="integrations.terraformBackend.hcpCloud.list.table.edit" />
            <PenIcon size={16} className="ml-1" />
          </Button>
        </Link>
      ),
      size: 15,
    }),
    columnHelper.accessor('id', {
      header: '',
      cell: ({ getValue }) => (
        <Button
          variant="link"
          onClick={() => deleteToken(getValue())}
          className="text-xs"
        >
          <FormattedMessage id="integrations.terraformBackend.hcpCloud.list.table.delete" />
          <Trash2Icon size={16} className="ml-1" />
        </Button>
      ),
      size: 15,
    }),
  ] as ColumnDef<HCPCloudTokenForList>[];
